import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Field, Form } from 'react-final-form';
import { FormGroup, FormGroupField, FormGroupLabel, FormButtonToolbar, FormContainer } from './FormElements';
import { CardTitle, CardTitleWrap } from '../Card';
import { Button, ButtonWithLoading } from '../Button';
import FormField from './FormField';
import Input from './Input';
import CheckBox from './CheckBox';
import DatePickers from './date-pickers/DatePicker';
import DateTimePicker from './date-pickers/DateTimePicker';
import Select from './Select';
import SearchSelect from './SearchSelect';
import SelectWithModal from './SelectWithModal';
import Textarea from './Textarea';
import CurrencyInput from './CurrencyInput';
import IntegerInput from './IntegerInput';
import MaskedInput from './MaskedInput';
import UrlActionField from './UrlActionField';

export const componentMap = {
  FormField,
  Input,
  CheckBox,
  DatePickers,
  DateTimePicker,
  Select,
  SearchSelect,
  SelectWithModal,
  Textarea,
  CurrencyInput,
  IntegerInput,
  MaskedInput,
  UrlActionField,
};

const DynamicForm = ({
  structure,
  config,
  sections = [],
  isViewMode = false,
  isEditing = false,
  searchFunctions = {},
  customCallbacks = {},
  modalComponents = {},
  modalProps = {},
  onSubmit,
  initialData,
  initialValues,
  submitButtonText = 'Salvar',
  submitButtonLoading = false,
  cancelButtonText = 'Cancelar',
  onCancel,
  submitButtonVariant = 'primary',
  cancelButtonVariant = 'secondary',
  showSectionHeaders = true,
  formClassName,
}) => {
  const getSectionKey = (section, sectionIndex) => {
    const sectionIdentity = section?.id ?? section?.title;
    return sectionIdentity ? `section-${sectionIdentity}` : `section-${sectionIndex}`;
  };

  const getFieldKey = (fieldConfig, fieldIndex) => {
    const fieldIdentity = fieldConfig?.name ?? fieldConfig?.id;
    return fieldIdentity ? `field-${fieldIdentity}` : `field-${fieldIndex}`;
  };

  // compat: allow consumers to pass a flat "config" array instead of structure/sections
  let resolvedStructure = structure;
  let resolvedSections = sections;

  if (!resolvedStructure && Array.isArray(config)) {
    const mapTypeToComponent = (field) => {
      if (field.component) return field.component;
      switch (field.type) {
        case 'textarea':
          return 'Textarea';
        case 'select':
          return 'Select';
        case 'search-select':
          return 'SearchSelect';
        case 'checkbox':
          return 'CheckBox';
        case 'date':
          return 'DatePickers';
        case 'datetime-local':
          return 'DateTimePicker';
        case 'currency':
          return 'CurrencyInput';
        case 'integer':
          return 'IntegerInput';
        case 'masked':
          return 'MaskedInput';
        default:
          return 'Input';
      }
    };

    const convertedRow = config.map((field) => ({
      colProps: { md: 12, ...(field.colProps || {}) },
      ...field,
      component: mapTypeToComponent(field),
    }));

    resolvedStructure = [convertedRow];
    resolvedSections = sections && sections.length ? sections : [{ title: '', rows: [0] }];
  }

  if (!resolvedStructure || !Array.isArray(resolvedStructure)) {
    return <div>Erro: Estrutura do formulario invalida</div>;
  }

  if (!resolvedSections || !Array.isArray(resolvedSections)) {
    return <div>Erro: Secoes do formulario invalidas</div>;
  }

  const renderField = (fieldConfig) => {
    const FieldComponent = componentMap[fieldConfig.component];

    if (!FieldComponent) {
      return null;
    }

    const {
      name,
      type,
      label,
      placeholder,
      component,
      colProps,
      readOnly,
      disabled,
      customOnChange,
      ...restProps
    } = fieldConfig;

    const isDisabled = disabled !== undefined ? disabled : isViewMode || (readOnly && isEditing);

    if (fieldConfig.component === 'CheckBox') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'Select') {
     /*  return <Field name={fieldConfig.name} component={FieldComponent} type={fieldConfig.type} label={fieldConfig.label} placeholder={fieldConfig.placeholder} disabled={isDisabled} parse={fieldConfig.parse} format={fieldConfig.format} {...restProps} />; */
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'SearchSelect') {
      const onSearch = searchFunctions[fieldConfig.name] || fieldConfig.onSearch;
      const callback = customCallbacks[fieldConfig.name];
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          onSearch={onSearch}
          customOnChange={callback}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'SelectWithModal') {
      const modalComponent = modalComponents[fieldConfig.name] || fieldConfig.modalComponent;
      const modalProp = modalProps[fieldConfig.name] || fieldConfig.modalProps || {};
      return <Field name={fieldConfig.name} component={FieldComponent} label={fieldConfig.label} placeholder={fieldConfig.placeholder} disabled={isDisabled} modalComponent={modalComponent} modalProps={modalProp} {...restProps} />;
    }

    if (fieldConfig.component === 'Textarea') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          rows={fieldConfig.rows}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'Input') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'MaskedInput') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'DateTimePicker') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          {...restProps}
        />
      );
    }

    if (fieldConfig.component === 'DatePickers') {
      return (
        <Field
          name={fieldConfig.name}
          component={FieldComponent}
          type={fieldConfig.type}
          label={fieldConfig.label}
          placeholder={fieldConfig.placeholder}
          disabled={isDisabled}
          readOnly={readOnly}
          {...restProps}
        />
      );
    }

    return (
      <>
        <FormGroupLabel>{fieldConfig.label}</FormGroupLabel>
        <FormGroupField>
          <Field
            name={fieldConfig.name}
            component={FieldComponent}
            type={fieldConfig.type}
            placeholder={fieldConfig.placeholder}
            disabled={isDisabled}
            {...restProps}
          />
        </FormGroupField>
      </>
    );
  };

  const renderContent = () =>
    resolvedSections.map((section, sectionIndex) => (
      <React.Fragment key={getSectionKey(section, sectionIndex)}>
        {showSectionHeaders && (
          <CardTitleWrap className="mb-3 mt-1">
            <CardTitle>{section.title}</CardTitle>
          </CardTitleWrap>
        )}
        {section.rows.map((rowIndex) => {
          if (!resolvedStructure || !resolvedStructure[rowIndex] || !Array.isArray(resolvedStructure[rowIndex])) {
            return null;
          }

          return (
            <FormGroup key={rowIndex}>
              <Row>
                {resolvedStructure[rowIndex].map((fieldConfig, fieldIndex) => {
                  if (!fieldConfig) {
                    return null;
                  }

                  return (
                    <Col key={getFieldKey(fieldConfig, fieldIndex)} {...fieldConfig.colProps}>
                      {renderField(fieldConfig)}
                    </Col>
                  );
                })}
              </Row>
            </FormGroup>
          );
        })}
      </React.Fragment>
    ));

  // If onSubmit is provided, wrap with react-final-form for standalone usage (common for config-based forms)
  if (onSubmit) {
    const initial = initialValues || initialData || {};

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={initial}
        render={({ handleSubmit }) => (
          <FormContainer onSubmit={handleSubmit} className={formClassName}>
            {renderContent()}

            <FormButtonToolbar className="mt-3">
              <ButtonWithLoading
                variant={submitButtonVariant}
                type="submit"
                loading={submitButtonLoading ? 'true' : undefined}
              >
                {submitButtonText}
              </ButtonWithLoading>
              {onCancel && (
                <Button variant={cancelButtonVariant} type="button" onClick={onCancel}>
                  {cancelButtonText}
                </Button>
              )}
            </FormButtonToolbar>
          </FormContainer>
        )}
      />
    );
  }

  return renderContent();
};

export default DynamicForm;
